import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Camera, Download, Wand2, Sparkles, Scissors, MoreHorizontal, Archive, Trash2, Grid3x3, Images } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { api, ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';
import { CompositorDialog } from '@/features/compositor/CompositorDialog';
import { PhotoViewerDialog, type SlotEntry } from '@/features/photo-viewer/PhotoViewerDialog';
import { isExteriorGuide } from '@bgswap/shared';

interface ShotSlot { key: string; guide: string; enabled: boolean }

interface CarDetail {
  id: string;
  stockNumber: string | null;
  vin: string | null;
  year: number | null;
  make: string | null;
  model: string | null;
  color: string | null;
  status: string;
  pendingJobs?: number;
  photos: Array<{
    id: string;
    slotIndex: number;
    slotKey: string | null;
    stage: 'ORIGINAL' | 'CLEAN' | 'FINAL';
    url: string;
    width: number | null;
    height: number | null;
  }>;
}

export function CarDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [compositorOpen, setCompositorOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerSlotIndex, setViewerSlotIndex] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<'all' | 'gallery'>(() => {
    if (typeof window === 'undefined') return 'all';
    return (localStorage.getItem('bgswap:car-view-mode') as 'all' | 'gallery') || 'all';
  });
  useEffect(() => {
    localStorage.setItem('bgswap:car-view-mode', viewMode);
  }, [viewMode]);

  const car = useQuery({
    queryKey: ['car', id],
    queryFn: () => api.get<CarDetail>(`/cars/${id}`),
    enabled: !!id,
    // While cutouts are still being processed in the background, repoll so
    // the green "clean" badges appear without a manual refresh.
    refetchInterval: (q) => ((q.state.data?.pendingJobs ?? 0) > 0 ? 3000 : false),
  });

  const pkg = useQuery({
    queryKey: ['photo-package'],
    queryFn: () => api.get<ShotSlot[]>('/tenant/photo-package'),
  });
  const guideByKey = new Map((pkg.data ?? []).map((s) => [s.key, s.guide] as const));
  const isCuttable = (slotKey: string | null) =>
    slotKey ? isExteriorGuide(guideByKey.get(slotKey) ?? null) : false;

  const cutoutAll = useMutation({
    mutationFn: () => api.post<{ queued: number }>(`/cars/${id}/cutouts`),
    onSuccess: (r) => {
      if (r.queued === 0) {
        toast.info('Every photo already has a cutout.');
      } else {
        toast.success(`Queued ${r.queued} cutout${r.queued === 1 ? '' : 's'} — they'll appear here as they finish.`);
      }
      qc.invalidateQueries({ queryKey: ['car', id] });
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Could not queue cutouts'),
  });

  const cutoutOne = useMutation({
    mutationFn: (photoId: string) => api.post<{ jobId: string | null; alreadyDone: boolean }>(`/photos/${photoId}/cutout`),
    onSuccess: (r) => {
      if (r.alreadyDone) toast.info('Already has a cutout.');
      else toast.success('Cutout queued.');
      qc.invalidateQueries({ queryKey: ['car', id] });
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Could not queue cutout'),
  });

  const archive = useMutation({
    mutationFn: () => api.post(`/cars/${id}/archive`),
    onSuccess: () => {
      toast.success('Car archived');
      qc.invalidateQueries({ queryKey: ['cars'] });
      navigate('/app/cars');
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Could not archive'),
  });

  const remove = useMutation({
    mutationFn: () => api.delete(`/cars/${id}`),
    onSuccess: () => {
      toast.success('Car deleted');
      qc.invalidateQueries({ queryKey: ['cars'] });
      navigate('/app/cars');
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Could not delete'),
  });

  // ===== Slot/viewer derivations =====
  // IMPORTANT: every hook runs unconditionally on every render — never put a
  // hook after an early return, or React's hook-count check will throw #310.
  // The data may be undefined here; useMemo handles the empty case gracefully.
  const photos = car.data?.photos;
  const labelByKey = useMemo(() => {
    return new Map((pkg.data ?? []).map((s) => [s.key, (s as ShotSlot & { label?: string }).label] as const));
  }, [pkg.data]);

  const slots = useMemo(() => {
    type Photo = NonNullable<typeof photos>[number];
    type Stages = Partial<Record<'ORIGINAL' | 'CLEAN' | 'FINAL', Photo>>;
    if (!photos) return [] as Array<[number, Stages]>;
    const bySlot = new Map<number, Stages>();
    for (const p of photos) {
      if (!bySlot.has(p.slotIndex)) bySlot.set(p.slotIndex, {});
      bySlot.get(p.slotIndex)![p.stage] = p;
    }
    return [...bySlot.entries()].sort((a, b) => a[0] - b[0]);
  }, [photos]);

  const viewerSlots: SlotEntry[] = useMemo(() => {
    return slots.map(([slotIndex, stages]) => {
      const slotKey = (stages.ORIGINAL ?? stages.CLEAN ?? stages.FINAL)?.slotKey ?? null;
      return {
        slotIndex,
        slotKey,
        label: slotKey ? labelByKey.get(slotKey) : undefined,
        stages: {
          ORIGINAL: stages.ORIGINAL ? { id: stages.ORIGINAL.id, url: stages.ORIGINAL.url, width: stages.ORIGINAL.width, height: stages.ORIGINAL.height } : undefined,
          CLEAN:    stages.CLEAN    ? { id: stages.CLEAN.id,    url: stages.CLEAN.url,    width: stages.CLEAN.width,    height: stages.CLEAN.height    } : undefined,
          FINAL:    stages.FINAL    ? { id: stages.FINAL.id,    url: stages.FINAL.url,    width: stages.FINAL.width,    height: stages.FINAL.height    } : undefined,
        },
      };
    });
  }, [slots, labelByKey]);

  if (car.isLoading) {
    return <div className="container py-8 space-y-4"><Skeleton className="h-12 w-64" /><Skeleton className="h-96" /></div>;
  }
  if (!car.data) return null;
  const c = car.data;

  const hasCleanPhotos = c.photos.some((p) => p.stage === 'CLEAN');
  // Gallery view: only slots that already have a FINAL composite.
  const gallerySlots = slots.filter(([, s]) => s.FINAL);
  // Only exterior shots are cutout-eligible. Wheels and interior stay as-is.
  const needsCutout = slots.some(
    ([, s]) => s.ORIGINAL && !s.CLEAN && isCuttable(s.ORIGINAL.slotKey),
  );
  const pendingJobs = c.pendingJobs ?? 0;

  return (
    <div className="container py-8 space-y-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="text-xs text-muted-foreground">Stock # {c.stockNumber ?? '—'}</div>
          <h1 className="text-3xl font-bold tracking-tight">
            {[c.year, c.make, c.model].filter(Boolean).join(' ') || 'Unnamed car'}
          </h1>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            {c.vin && <span>VIN {c.vin}</span>}
            {c.color && <span>· {c.color}</span>}
            <Badge variant="outline" className="ml-1">{c.status.toLowerCase().replace('_', ' ')}</Badge>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button asChild className="gap-2">
            <Link to={`/app/cars/${c.id}/capture`}><Camera className="h-4 w-4" /> Capture</Link>
          </Button>
          <Button
            variant="outline"
            className="gap-2"
            disabled={!needsCutout || cutoutAll.isPending || pendingJobs > 0}
            onClick={() => cutoutAll.mutate()}
          >
            <Scissors className="h-4 w-4" />
            {pendingJobs > 0
              ? `Processing ${pendingJobs}…`
              : cutoutAll.isPending
              ? 'Queueing…'
              : 'Remove backgrounds'}
          </Button>
          <Button variant="outline" className="gap-2" disabled={!hasCleanPhotos} onClick={() => setCompositorOpen(true)}>
            <Wand2 className="h-4 w-4" /> Apply studio
          </Button>
          <Button variant="outline" className="gap-2" onClick={() => exportCar(c.id)}>
            <Download className="h-4 w-4" /> Export ZIP
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" title="More">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {c.status !== 'ARCHIVED' && (
                <DropdownMenuItem
                  onSelect={() => archive.mutate()}
                  disabled={archive.isPending}
                >
                  <Archive className="mr-2 h-4 w-4" /> Archive
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={(e) => { e.preventDefault(); setDeleteOpen(true); }}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" /> Delete car
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {pendingJobs > 0 && (
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="flex items-center gap-3 py-3 text-sm">
            <Sparkles className="h-4 w-4 animate-pulse text-primary" />
            <span>
              Removing backgrounds for {pendingJobs} photo{pendingJobs === 1 ? '' : 's'} —
              this page refreshes automatically.
            </span>
          </CardContent>
        </Card>
      )}

      {slots.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center space-y-3">
            <div className="text-lg font-medium">No photos yet.</div>
            <div className="text-sm text-muted-foreground">Start a capture session to shoot the full photo package.</div>
            <Button asChild className="gap-2">
              <Link to={`/app/cars/${c.id}/capture`}><Camera className="h-4 w-4" /> Start capture</Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* View mode toggle */}
          <div className="flex items-center justify-between">
            <div className="text-xs text-muted-foreground">
              {viewMode === 'gallery'
                ? `${gallerySlots.length} finished ${gallerySlots.length === 1 ? 'photo' : 'photos'}`
                : `${slots.length} ${slots.length === 1 ? 'slot' : 'slots'} · click to compare originals, cutouts and finals`}
            </div>
            <div className="inline-flex rounded-md border border-border bg-card p-0.5 text-xs">
              <button
                onClick={() => setViewMode('all')}
                className={cn(
                  'flex items-center gap-1.5 rounded-sm px-2.5 py-1 transition-colors',
                  viewMode === 'all' ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <Grid3x3 className="h-3.5 w-3.5" /> All slots
              </button>
              <button
                onClick={() => setViewMode('gallery')}
                className={cn(
                  'flex items-center gap-1.5 rounded-sm px-2.5 py-1 transition-colors',
                  viewMode === 'gallery' ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <Images className="h-3.5 w-3.5" /> Gallery
              </button>
            </div>
          </div>

          {viewMode === 'gallery' && gallerySlots.length === 0 ? (
            <Card>
              <CardContent className="space-y-2 py-12 text-center text-sm text-muted-foreground">
                <div className="text-lg font-medium text-foreground">No finished photos yet.</div>
                Apply a studio background and overlay to your cutouts to populate the gallery.
                {hasCleanPhotos && (
                  <div className="pt-2">
                    <Button onClick={() => setCompositorOpen(true)} className="gap-2">
                      <Wand2 className="h-4 w-4" /> Apply studio
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <div
              className={cn(
                'grid gap-4',
                viewMode === 'gallery' ? 'sm:grid-cols-1 md:grid-cols-2' : 'sm:grid-cols-2 lg:grid-cols-3',
              )}
            >
              {(viewMode === 'gallery' ? gallerySlots : slots).map(([slotIndex, stages]) => {
                const primary = stages.FINAL || stages.CLEAN || stages.ORIGINAL;
                const slotNeedsCutout = !!stages.ORIGINAL && !stages.CLEAN;
                return (
                  <Card
                    key={slotIndex}
                    className="cursor-zoom-in overflow-hidden transition-colors hover:border-primary/40"
                    onClick={() => { setViewerSlotIndex(slotIndex); setViewerOpen(true); }}
                  >
                    <div className={cn(
                      'relative bg-gradient-to-br from-muted to-secondary',
                      viewMode === 'gallery' ? 'aspect-[16/10]' : 'aspect-[4/3]',
                    )}>
                      {primary && <img src={primary.url} alt="" className="h-full w-full object-cover" />}
                      <div className="absolute left-2 top-2 flex gap-1">
                        {stages.ORIGINAL && <Badge variant="outline" className="text-[10px]">orig</Badge>}
                        {stages.CLEAN && <Badge variant="secondary" className="text-[10px]">clean</Badge>}
                        {stages.FINAL && <Badge variant="success" className="text-[10px]">final</Badge>}
                      </div>
                      {slotNeedsCutout && stages.ORIGINAL && isCuttable(stages.ORIGINAL.slotKey) && (
                        <button
                          onClick={(e) => { e.stopPropagation(); cutoutOne.mutate(stages.ORIGINAL!.id); }}
                          disabled={cutoutOne.isPending}
                          className="absolute right-2 top-2 flex items-center gap-1 rounded-full bg-black/70 px-2 py-1 text-[10px] text-white/90 backdrop-blur transition-colors hover:bg-black/85 disabled:opacity-60"
                          title="Remove background for this photo"
                        >
                          <Scissors className="h-3 w-3" /> Cutout
                        </button>
                      )}
                    </div>
                    <CardContent className="py-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium">Slot {String(slotIndex).padStart(2, '0')}</span>
                        <span className="truncate text-xs text-muted-foreground">{(primary?.slotKey ?? '').replace(/_/g, ' ')}</span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}

      <PhotoViewerDialog
        open={viewerOpen}
        onOpenChange={setViewerOpen}
        slots={viewerSlots}
        carId={c.id}
        initialSlotIndex={viewerSlotIndex}
        canCutout={isCuttable}
      />

      <CompositorDialog
        open={compositorOpen}
        onOpenChange={setCompositorOpen}
        carId={c.id}
        photos={c.photos}
      />

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete this car?</DialogTitle>
            <DialogDescription>
              All photos and composites for{' '}
              <span className="font-semibold text-foreground">
                {[c.year, c.make, c.model].filter(Boolean).join(' ') || c.stockNumber || c.vin || 'this car'}
              </span>{' '}
              will be permanently removed. This can't be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost">Cancel</Button>
            </DialogClose>
            <Button
              variant="destructive"
              disabled={remove.isPending}
              onClick={() => remove.mutate()}
            >
              {remove.isPending ? 'Deleting…' : 'Delete car'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

async function exportCar(id: string) {
  const res = await fetch(`/api/v1/cars/${id}/export`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  });
  if (!res.ok) return;
  const blob = await res.blob();
  const cd = res.headers.get('Content-Disposition') ?? '';
  const m = /filename="([^"]+)"/.exec(cd);
  const name = m?.[1] ?? `car-${id}.zip`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
